import commands from '@/constants/commands';
import { AreaSubCmds } from '@/constants/sub-commands';
import { useLocale } from '@/contexts/localeContext';
import { TranslationKeys } from '@/contexts/TranslationKeys';
import { AccessArea } from '@/models/AccessArea';
import { ExecuteCommand } from '@/models/ExecuteCommand';
import { deleteAreaLocation, getAccessAreaTree, getAreaLocations, storeAreaLocation } from '@/services/accessAreaService';
import { executeCommandAreaTree } from '@/services/executeCommandService';
import { showError } from '@/services/utilities/ServiceError';
import { getZoneNamesWithPagination } from '@/services/zoneService';
import { appendFilter } from '@/utilities/filters';
import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { faBuilding, faDoorClosed, faLayerGroup, faHdd } from '@fortawesome/free-solid-svg-icons';
import CheckboxTree2, { Tree } from '../checkbox-tree/checkbox-tree2/checkbox-tree2';

interface AccessAreaDetailsProps {
    accessArea: AccessArea;
}

const AccessAreaDetails: FC<AccessAreaDetailsProps> = ({ accessArea }) => {
    const { translate } = useLocale();
    const [topTree, setTopTree] = useState<Tree[]>([]);

    useEffect(() => {
        setTopTree([
            {
                id: accessArea.id.toString(),
                label: accessArea.name,
                lookupname: `accessArea${accessArea.id}`,
                clickable: false,
                canExpand: true,
            }
        ]);
    }, [accessArea]);

    const onExpand = async (node: Tree) => {
        // If the collapsed node already has children, we don't need to fetch data for it again.
        if (node.children && node.children.length > 0) {
            return;
        }

        const children = await getChildren(node);
        node.children = children;
        setTopTree([...topTree]);
    };

    const onCheck = async (node: Tree, isChecked: boolean) => {
        try {
            let subCommand: string = "", key: "controllerId" | "zoneId" | "buildingId" = "controllerId";

            if (node.lookupname.match(/^accessArea\d+\/controller\d+$/)) {
                subCommand = isChecked ? AreaSubCmds.ADD_CONTROLLER_AREA_LOCATION : AreaSubCmds.DELETE_CONTROLLER_AREA_LOCATION;
                key = "controllerId";

                performOperation(subCommand, key, node.id);
            }
            else if (node.lookupname.match(/^accessArea\d+\/building\d+$/)) {
                subCommand = isChecked ? AreaSubCmds.ADD_BUILDING_AREA_LOCATION : AreaSubCmds.DELETE_BUILDING_AREA_LOCATION;
                key = "buildingId";

                performOperation(subCommand, key, node.id);
            }
            else if (node.lookupname.match(/^accessArea\d+\/controller\d+\/zone\d+$/) || node.lookupname.match(/^accessArea\d+\/building\d+\/zone\d+$/)) {
                subCommand = isChecked ? AreaSubCmds.ADD_ZONE_AREA_LOCATION : AreaSubCmds.DELETE_ZONE_AREA_LOCATION;
                const trigger = node.lookupname.includes('controller') ? "CONTROLLER" : "BUILDING";
                const buildingId = node.lookupname.match(/^accessArea\d+\/building\d+\/zone\d+$/) ? node.lookupname.split('/')[1].replace("building", "") : undefined;
                key = "zoneId";

                performOperation(subCommand, key, node.id, trigger, buildingId);
            }
            else if (node.lookupname.match(/^accessArea\d+\/controller\d+\/zone\d+\/door\d+$/) || node.lookupname.match(/^accessArea\d+\/building\d+\/zone\d+\/door\d+$/)) {
                if (isChecked) {
                    await storeAreaLocation(accessArea.id.toString(), node.id.toString());
                }
                else {
                    const locationFilter = appendFilter(node.id, "Location", "location.id", []);
                    const areaFilter = appendFilter(accessArea.id, "Area", "area.id", locationFilter);
                    const areaLocation = await getAreaLocations({ filters: areaFilter });

                    if (areaLocation && areaLocation.length > 0) {
                        await deleteAreaLocation(areaLocation[0].id);
                    }
                }
            }
        } catch (error) {
            toast.warn(translate.byKeyFormatted(TranslationKeys.failedToUpdateAccessAreaTree, { nodeName: node.label }));
        }
    }
    
    const performOperation = async (cmdSubType: string, key: "controllerId" | "zoneId" | "buildingId", nodeId: string, accessAreaTrigger?: "BUILDING" | "CONTROLLER", buildingId?: string) => {
        try {
            const executeCommandData: ExecuteCommand = {
                cmd: commands.Maintenance.label,
                cmdSubType: cmdSubType,
                [key]: nodeId,
                areaId: accessArea.id.toString(),
                ...(accessAreaTrigger && { accessAreaTreeMainParent: accessAreaTrigger }),
                ...(buildingId && { buildingId: buildingId })
            };

            await executeCommandAreaTree(executeCommandData)
        } catch (err) {
            showError(err, toast.warn)
        }
    }

    const getChildren = async (node: Tree): Promise<Tree[]> => {
        let children: { id: number, name: string, checked: boolean, type: string }[] = [];

        try {

            if (node.lookupname.match(/^accessArea\d+$/)) {
                children = await getAccessAreaTree(node.id, "ACCESS_AREA", accessArea.id);

                return await Promise.all(children.map(async (item) => ({
                    id: item.id.toString(),
                    label: item.name,
                    lookupname: item.type === "CONTROLLER" ? `${node.lookupname}/controller${item.id}` : `${node.lookupname}/building${item.id}`,
                    clickable: true,
                    canExpand: true,
                    checked: item.checked,
                    icon: item.type === "CONTROLLER" ? faHdd : faBuilding,
                    key: item.type === "CONTROLLER" ? "controller" : "building",
                })));
            }
            else if (node.lookupname.match(/^accessArea\d+\/controller\d+$/) || node.lookupname.match(/^accessArea\d+\/building\d+$/)) {
                const nodeType = node.lookupname.includes('controller') ? "CONTROLLER" : "BUILDING";
                children = await getAccessAreaTree(node.id, nodeType, accessArea.id);

                return await Promise.all(children.map(async (item) => ({
                    id: item.id.toString(),
                    label: item.name,
                    lookupname: `${node.lookupname}/zone${item.id}`,
                    clickable: true,
                    canExpand: true,
                    checked: item.checked,
                    icon: faLayerGroup,
                    key: "zone",
                })))
            }
            else if (node.lookupname.match(/^accessArea\d+\/controller\d+\/zone\d+$/) || node.lookupname.match(/^accessArea\d+\/building\d+\/zone\d+$/)) {
                const buildingId = node.lookupname.match(/^accessArea\d+\/building\d+\/zone\d+$/) ? node.lookupname.split('/')[1].replace("building", "") : undefined;
                children = await getAccessAreaTree(node.id, "ZONE", accessArea.id, buildingId ? parseInt(buildingId) : undefined);

                return children.map((item) => ({
                    id: item.id.toString(),
                    label: item.name,
                    lookupname: `${node.lookupname}/door${item.id}`,
                    clickable: true,
                    canExpand: false,
                    checked: item.checked,
                    icon: faDoorClosed,
                    key: "door",
                }))
            }
        } catch (error) {
            toast.warn(translate.byKeyFormatted(TranslationKeys.failedToLoadChildrenFor, { nodeName: node.label }));
        }
        
        node.children = [];
        return node.children;
    }

    return <CheckboxTree2 tree={topTree} onExpand={onExpand} onCheck={onCheck} />
}

export default AccessAreaDetails;
